// Market-shaped waste catalog (CatalogOptions) built from the shared core catalog,
// plus the in-memory list of "request a missing waste type" submissions.

import type {
  CatalogCategoryOption,
  CatalogChangeRequestPayload,
  CatalogMaterialOption,
  CatalogOptions,
} from '../../types/waste-catalog.type';
import {
  DEMO_CATEGORIES,
  DEMO_MATERIALS,
  categoryByCode,
  demoId,
  ids,
  iso,
  materialById,
  type DemoMaterial,
} from '../core';

// Kind codes for catalog rows the shared core has no kind for. Market-local:
// no other app references these ids.
const REGULATORY_CODE_KIND = 'ae';
const CATALOG_META_KIND = 'af';

export const CATALOG_REVISION_ID = demoId(CATALOG_META_KIND, 100);
export const CATALOG_REVISION_NUMBER = 7;

/** EWC (European Waste Catalogue) code -> [description_en, description_ar]. */
const EWC_DESCRIPTIONS: Record<string, [string, string]> = {
  '15 01 01': ['Paper and cardboard packaging', 'عبوات الورق والكرتون'],
  '15 01 02': ['Plastic packaging', 'عبوات بلاستيكية'],
  '15 01 03': ['Wooden packaging', 'عبوات خشبية'],
  '15 01 04': ['Metallic packaging', 'عبوات معدنية'],
  '15 01 05': ['Composite packaging', 'عبوات مركبة'],
  '15 01 06': ['Mixed packaging', 'عبوات مختلطة'],
  '15 01 07': ['Glass packaging', 'عبوات زجاجية'],
  '15 01 10*': ['Packaging containing residues of or contaminated by hazardous substances', 'عبوات تحتوي على بقايا مواد خطرة أو ملوثة بها'],
  '20 01 01': ['Paper and cardboard', 'ورق وكرتون'],
  '20 01 08': ['Biodegradable kitchen and canteen waste', 'مخلفات المطابخ والمقاصف القابلة للتحلل'],
  '20 01 10': ['Clothes', 'ملابس'],
  '20 01 11': ['Textiles', 'منسوجات'],
  '20 01 25': ['Edible oil and fat', 'زيوت ودهون صالحة للأكل'],
  '20 01 33*': ['Batteries and accumulators', 'بطاريات ومراكم'],
  '20 01 36': ['Discarded electrical and electronic equipment', 'معدات كهربائية وإلكترونية مهملة'],
  '20 01 39': ['Plastics', 'بلاستيك'],
  '20 02 01': ['Biodegradable garden and park waste', 'مخلفات الحدائق والمتنزهات القابلة للتحلل'],
  '20 03 01': ['Mixed municipal waste', 'نفايات بلدية مختلطة'],
  '17 01 01': ['Concrete', 'خرسانة'],
  '17 02 01': ['Wood', 'خشب'],
  '17 02 03': ['Plastic', 'بلاستيك'],
  '17 04 01': ['Copper, bronze, brass', 'نحاس وبرونز ونحاس أصفر'],
  '17 04 05': ['Iron and steel', 'حديد وصلب'],
  '17 08 02': ['Gypsum-based construction materials', 'مواد بناء أساسها الجبس'],
  '03 01 05': ['Sawdust, shavings, cuttings and wood', 'نشارة ونجارة وقصاصات الخشب'],
  '08 01 11*': ['Waste paint and varnish containing organic solvents', 'مخلفات الطلاء والورنيش المحتوية على مذيبات عضوية'],
  '08 03 18': ['Waste printing toner', 'مخلفات أحبار الطباعة'],
  '13 02 05*': ['Mineral-based non-chlorinated engine, gear and lubricating oils', 'زيوت محركات وتروس وتشحيم معدنية غير مكلورة'],
  '14 06 03*': ['Other solvents and solvent mixtures', 'مذيبات وخلائط مذيبات أخرى'],
  '16 01 03': ['End-of-life tyres', 'إطارات منتهية الصلاحية'],
  '16 02 14': ['Discarded equipment', 'معدات مهملة'],
  '19 12 04': ['Plastic and rubber', 'بلاستيك ومطاط'],
};

/** One regulatory code row per distinct EWC code, numbered in catalog order. */
const EWC_CODES: string[] = Array.from(new Set(DEMO_MATERIALS.flatMap((m) => m.ewc_codes)));

export const REGULATORY_CODE_IDS: Record<string, string> = Object.fromEntries(
  EWC_CODES.map((code, i) => [code, demoId(REGULATORY_CODE_KIND, i + 1)]),
);

export interface MwanSourceCategory {
  id: string;
  name: string;
}

/** MWAN (National Center for Waste Management) source categories. */
const MWAN = {
  commercial: { id: demoId(CATALOG_META_KIND, 1), name: 'Commercial & Industrial Waste' },
  municipal: { id: demoId(CATALOG_META_KIND, 2), name: 'Municipal Solid Waste' },
  construction: { id: demoId(CATALOG_META_KIND, 3), name: 'Construction & Demolition Waste' },
  hazardous: { id: demoId(CATALOG_META_KIND, 4), name: 'Hazardous Waste' },
} satisfies Record<string, MwanSourceCategory>;

export function mwanSourceFor(material: DemoMaterial): MwanSourceCategory {
  if (material.hazardous) return MWAN.hazardous;
  if (material.category_code === 'construction') return MWAN.construction;
  if (material.category_code === 'organic' || material.category_code === 'mixed') return MWAN.municipal;
  return MWAN.commercial;
}

export interface RegulatoryMapping {
  regulatory_code_id: string;
  code: string;
  description_en: string;
  description_ar: string;
  hazardous_marker: string;
  is_default: boolean;
}

export function regulatoryMappings(material: DemoMaterial): RegulatoryMapping[] {
  return material.ewc_codes.map((raw, i) => {
    const [description_en, description_ar] = EWC_DESCRIPTIONS[raw] ?? [material.name_en, material.name_ar];
    return {
      regulatory_code_id: REGULATORY_CODE_IDS[raw],
      code: raw.replace('*', '').trim(),
      description_en,
      description_ar,
      hazardous_marker: raw.endsWith('*') ? 'hazardous' : 'non_hazardous',
      is_default: i === 0,
    };
  });
}

export function catalogMaterialOption(material: DemoMaterial): CatalogMaterialOption {
  const category = categoryByCode(material.category_code);
  const mwan = mwanSourceFor(material);
  return {
    id: material.id,
    code: material.code,
    slug: material.slug,
    name: material.name_en,
    name_en: material.name_en,
    name_ar: material.name_ar,
    category_id: category?.id ?? '',
    category_code: material.category_code,
    mwan_source_category_id: mwan.id,
    mwan_source_category_name: mwan.name,
    default_physical_state: material.default_physical_state,
    possible_regulatory_mappings: regulatoryMappings(material),
  };
}

/** The published catalog; scoped to `materialIds` (an organization's basket) when given. */
export function buildCatalogOptions(materialIds?: ReadonlySet<string>): CatalogOptions {
  const categories: CatalogCategoryOption[] = [...DEMO_CATEGORIES]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((category) => ({
      id: category.id,
      code: category.code,
      name: category.name_en,
      name_en: category.name_en,
      name_ar: category.name_ar,
      materials: DEMO_MATERIALS.filter(
        (m) => m.category_code === category.code && (!materialIds || materialIds.has(m.id)),
      ).map(catalogMaterialOption),
    }))
    .filter((category) => !materialIds || category.materials.length > 0);
  return {
    revision_id: CATALOG_REVISION_ID,
    revision_number: CATALOG_REVISION_NUMBER,
    categories,
  };
}

/** Classification snapshot a pre-order copies from its waste type at creation. */
export function classificationSnapshot(materialId: string, regulatoryCodeId?: string | null) {
  const material = materialById(materialId);
  if (!material) return null;
  const category = categoryByCode(material.category_code);
  const mappings = regulatoryMappings(material);
  const mapping = mappings.find((m) => m.regulatory_code_id === regulatoryCodeId) ?? mappings.find((m) => m.is_default) ?? mappings[0];
  const mwan = mwanSourceFor(material);
  return {
    material_id: material.id,
    material_type_id: material.material_type_id,
    material_category: material.category_code as string,
    material_name_snapshot: material.name_en,
    category_name_snapshot: category?.name_en ?? material.category_code,
    regulatory_code_id: mapping?.regulatory_code_id ?? null,
    regulatory_code_snapshot: mapping?.code ?? null,
    regulatory_code_description_snapshot: mapping?.description_en ?? null,
    mwan_source_category_id: mwan.id,
    mwan_source_category_snapshot: mwan.name,
    physical_state_snapshot: material.default_physical_state as string,
  };
}

// ── Catalog change requests ("request a missing waste type") ──────────────

export interface DemoChangeRequest extends CatalogChangeRequestPayload {
  id: string;
  status: 'submitted';
  created_at: string;
}

let changeRequests: DemoChangeRequest[] = [];

export function resetCatalog(): void {
  changeRequests = [];
}

export function createChangeRequest(payload: CatalogChangeRequestPayload): DemoChangeRequest {
  const row: DemoChangeRequest = {
    ...payload,
    id: ids.changeRequest(changeRequests.length + 1),
    status: 'submitted',
    created_at: iso(new Date()),
  };
  changeRequests = [...changeRequests, row];
  return row;
}

export function listChangeRequests(): readonly DemoChangeRequest[] {
  return changeRequests;
}
